import type {
  Anchor,
  ApiError,
  CreateThreadRequest,
  Status,
  Thread,
  ThreadsResponse,
} from '@annotate/shared';
import type { WidgetConfig } from './config.js';

export type Result<T> = { ok: true; value: T; warning?: string } | { ok: false; error: string };

async function request<T>(url: string, init?: RequestInit): Promise<Result<T>> {
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (err) {
    return { ok: false, error: `could not reach the annotate server: ${(err as Error).message}` };
  }

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    return { ok: false, error: `server returned something that is not JSON (${res.status})` };
  }

  if (!res.ok) {
    const err = json as ApiError;
    return { ok: false, error: err.detail || err.error || `server error ${res.status}` };
  }

  const payload = json as { warning?: string };
  return { ok: true, value: json as T, warning: payload.warning };
}

export class Api {
  constructor(private readonly config: WidgetConfig) {}

  private get pageUrl(): string {
    return `${window.location.origin}${window.location.pathname}`;
  }

  list(): Promise<Result<ThreadsResponse>> {
    const url = new URL('/api/threads', this.config.apiBase);
    url.searchParams.set('project', this.config.projectId);
    url.searchParams.set('url', this.pageUrl);
    return request<ThreadsResponse>(url.toString());
  }

  create(anchor: Anchor, body: string, authorName: string): Promise<Result<{ thread: Thread }>> {
    const payload: CreateThreadRequest = {
      projectId: this.config.projectId,
      pageUrl: this.pageUrl,
      pageTitle: document.title || null,
      anchor,
      body,
      authorName,
      viewport: { w: window.innerWidth, h: window.innerHeight },
    };
    return request<{ thread: Thread }>(new URL('/api/threads', this.config.apiBase).toString(), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  reply(threadId: string, body: string, authorName: string): Promise<Result<{ thread: Thread }>> {
    return request<{ thread: Thread }>(
      new URL(`/api/threads/${threadId}/comments`, this.config.apiBase).toString(),
      { method: 'POST', body: JSON.stringify({ body, authorName }) },
    );
  }

  setStatus(threadId: string, status: Status, authorName: string): Promise<Result<{ thread: Thread }>> {
    return request<{ thread: Thread }>(
      new URL(`/api/threads/${threadId}/status`, this.config.apiBase).toString(),
      { method: 'PATCH', body: JSON.stringify({ status, authorName }) },
    );
  }
}
